import { HalfYear, Quarter, YearMonth } from "data/period.data";
import { MenuType, StoreType, SubType } from "models/business.model";

export const convertSortParamData = (
  sortParam: string | null,
  colId?: string,
  isDisplay?: boolean
) => {
  const sortList = sortParam ? sortParam.split("|") : [];
  const sortData: { [key in string]: "asc" | "desc" } = sortList.length
    ? sortList.reduce((acc, key) => {
        const [k, v] = key.split(",");
        return { ...acc, [k]: v };
      }, {})
    : {};

  if (colId) {
    if (sortData[colId]) {
      const direction = sortData[colId];
      delete sortData[colId]; // 기존 정렬 삭제 (순서 변경)
      if (direction === "asc") {
        sortData[colId] = "desc";
      } else {
        sortData[colId] = "asc";
      }
    } else {
      if (!isDisplay) sortData[colId] = "asc";
    }
  }

  return sortData;
};

export const alignToJustify = (
  align: "left" | "center" | "right" | undefined
) => {
  switch (align) {
    case "left":
      return "flex-start";
    case "center":
      return "center";
    case "right":
      return "flex-end";
    default:
      return "flex-start";
  }
};

export const convertRegNumber = (regNumber: string) => {
  if (regNumber.length === 10) {
    return `${regNumber.slice(0, 3)}-${regNumber.slice(3, 5)}-${regNumber.slice(
      5,
      10
    )}`;
  }
  return regNumber;
};

export const convertMobileNo = (mobileNo: string) => {
  const digits = (mobileNo || "").replace(/[^0-9]/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  return mobileNo;
};

export const convertProgress = (completed: string, total: string) => {
  return `${completed}/${total}`;
};

type DateFormat = "YYYY-MM-DD" | "YYYY/MM/DD" | "YYYY.MM.DD";

export const convertDate = (date: string, format?: DateFormat) => {
  if (!date) return "";
  const dateWithoutTime = date.split("T")[0];
  const [year, month, day] = dateWithoutTime.split("-");
  switch (format) {
    case "YYYY/MM/DD":
      return `${year}/${month}/${day}`;
    case "YYYY.MM.DD":
      return `${year}.${month}.${day}`;
    default:
      return `${year}-${month}-${day}`;
  }
};

export const convertCurrency = (value: number) => {
  if (value === 0) return "0";
  return value?.toLocaleString("ko-KR");
};

export const sortList = (list: any[], spSort: string | null): any[] => {
  const sortData = convertSortParamData(spSort);

  const tmpList = list;
  if (spSort && sortData) {
    const sortKeys = Object.keys(sortData);
    const sortValues = Object.values(sortData);

    tmpList.sort((a, b) => {
      for (let i = sortKeys.length; i > 0; i--) {
        const key = sortKeys[i - 1];
        const value = sortValues[i - 1];
        const aKey = a[key] || "";
        const bKey = b[key] || "";
        if (aKey < bKey) return value === "asc" ? -1 : 1;
        if (aKey > bKey) return value === "asc" ? 1 : -1;
      }
      return 0;
    });
    return tmpList;
  } else {
    return tmpList;
  }
};

export const monthStartDate = (year: string, month: string): string =>
  new Date(+year, +month - 1, 2).toISOString()?.split("T")[0];
export const monthEndDate = (year: string, month: string): string =>
  new Date(+year, +month, 1).toISOString()?.split("T")[0];

export const convertQuarterAndHalfToDate = (quarter: Quarter | HalfYear) => {
  const [year, q] = quarter.split("-");

  switch (q) {
    case "Q1":
      return {
        startDate: monthStartDate(year, "1"),
        endDate: monthEndDate(year, "3"),
      };
    case "Q2":
      return {
        startDate: monthStartDate(year, "4"),
        endDate: monthEndDate(year, "6"),
      };
    case "Q3":
      return {
        startDate: monthStartDate(year, "7"),
        endDate: monthEndDate(year, "9"),
      };
    case "Q4":
      return {
        startDate: monthStartDate(year, "10"),
        endDate: monthEndDate(year, "12"),
      };
    case "H1":
      return {
        startDate: monthStartDate(year, "1"),
        endDate: monthEndDate(year, "6"),
      };
    case "H2":
      return {
        startDate: monthStartDate(year, "7"),
        endDate: monthEndDate(year, "12"),
      };
    default:
      return {
        startDate: "",
        endDate: "",
      };
  }
};

export const convertYearMonthToDate = (yearMonth: YearMonth) => {
  const [year, m] = yearMonth.split("-");

  return {
    startDate: monthStartDate(year, m),
    endDate: monthEndDate(year, m),
  };
};

/** 합계표 조회 API `date` 파라미터: 월(yyyyMM), 분기(yyyy + 1~4, Q 문자 없음). 반기 구간은 null. */
export const convertSearchDatesToInvoiceSumCheckDate = (
  startDate: string | null,
  endDate: string | null
): string | null => {
  if (!(startDate && endDate)) return null;
  const period = convertDateToPeriod(startDate, endDate);
  if (!period) return null;
  if (period.includes("-H")) return null;
  const quarter = period.match(/^(\d{4})-Q(\d)$/);
  if (quarter) return `${quarter[1]}${quarter[2]}`;
  const month = period.match(/^(\d{4})-(\d{2})$/);
  if (month) return `${month[1]}${month[2]}`;
  return null;
};

export const convertDateToPeriod = (
  startDate: string | null,
  endDate: string | null
): string => {
  if (!(startDate && endDate)) return "";
  const [startYear, startMonth] = startDate.split("-");
  const [endYear, endMonth] = endDate.split("-");

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      return `${startYear}-${startMonth}`;
    } else {
      switch (startMonth) {
        case "01":
          if (endMonth === "03") return `${startYear}-Q1`;
          else if (endMonth === "06") return `${startYear}-H1`; // 상반기
          else return "";
        case "04":
          if (endMonth === "06") return `${startYear}-Q2`;
          else return "";
        case "07":
          if (endMonth === "09") return `${startYear}-Q3`;
          else if (endMonth === "12") return `${startYear}-H2`; // 하반기
          else return "";
        case "10":
          if (endMonth === "12") return `${startYear}-Q4`;
          else return "";
        default:
          return "";
      }
    }
  } else return "";
};

export const convertBusinessType = (
  type: "일반과세자" | "면세사업자" | "법인사업자" | "간이과세자"
) => {
  switch (type) {
    case "일반과세자":
      return "일반";
    case "간이과세자":
      return "간이";
    case "면세사업자":
      return "면세";
    case "법인사업자":
      return "법인";
    default:
      return type;
  }
};

export const reverseArray = (arr: any[]) => {
  const tmpArr = [...arr];
  return tmpArr.reverse();
};

export const convertMenuTypeToStoreType = (menuType: MenuType): StoreType => {
  switch (menuType) {
    case "card":
      return "card";
    case "bank":
      return "bank";
    case "tax-buy":
      return "tax";
    case "tax-sales":
      return "tax";
    case "extra-buy":
      return "extra";
    case "extra-sales":
      return "extra";
    default:
      throw new Error(`Unknown menu type: ${menuType}`);
  }
};

export const extractSubType = (menuType: MenuType): SubType | undefined => {
  if (menuType.includes("-")) {
    const [type, subTypeLowerCase] = menuType.split("-") as [StoreType, string];
    if (type && subTypeLowerCase)
      return subTypeLowerCase.toUpperCase() as SubType;
    else return;
  } else return;
};
